import React, { useState } from "react";
import { Cloud, Loader2, Sparkles, Copy, Droplets, Wind } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const WeatherTab = ({ stationId, reload }) => {
  const [location, setLocation] = useState("Hamilton");
  const [wx, setWx] = useState(null);
  const [script, setScript] = useState("");
  const [busy, setBusy] = useState("");

  const getWeather = async () => {
    if (!location.trim()) return toast.error("Enter a location");
    setBusy("wx");
    try {
      const r = await api.get("/newsroom/weather", { params: { location } });
      setWx(r.data); setScript("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Weather lookup failed");
    } finally { setBusy(""); }
  };

  const writeScript = async () => {
    setBusy("script");
    try {
      const r = await api.post("/newsroom/weather/script", { location, station_id: stationId });
      setScript(r.data.script);
      if (!wx) setWx(r.data.weather);
      toast.success("Weather break written & saved to your stories");
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Script failed");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="newsroom-weather-tab">
      <div className="flex flex-wrap items-start gap-4 mb-6">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-display text-xl font-bold text-ink-primary">Weather Center</h2>
          <p className="text-xs text-ink-muted mt-0.5">Real Open-Meteo forecast data · Current conditions · 3-day outlook</p>
        </div>
        <div className="flex gap-2">
          <input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="newsroom-weather-location"
            className="rounded-lg border border-line bg-bg-surface px-3 py-2 text-sm text-ink-primary w-44 focus:outline-none focus:border-brand-amber/50" />
          <button onClick={getWeather} disabled={busy === "wx"} data-testid="newsroom-weather-get-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2.5 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
            {busy === "wx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Get Weather
          </button>
        </div>
      </div>

      {!wx ? (
        <div className="rounded-xl border border-dashed border-line p-16 text-center text-sm text-ink-muted">
          <Cloud className="h-10 w-10 mx-auto mb-2 opacity-40" />
          Enter a location and click "Get Weather"
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-cyan-500/10 to-transparent p-6 flex flex-wrap items-center gap-6" data-testid="newsroom-weather-current">
            <div>
              <div className="text-xs text-ink-muted">{wx.place.name}{wx.place.admin ? `, ${wx.place.admin}` : ""} · {wx.place.country}</div>
              <div className="font-display text-5xl font-bold text-ink-primary mt-1">{Math.round(wx.current.temp)}°F</div>
              <div className="text-sm text-cyan-200 mt-1">{wx.current.condition} · feels like {Math.round(wx.current.feels_like)}°F</div>
            </div>
            <div className="flex gap-6 text-xs text-ink-secondary">
              <span className="inline-flex items-center gap-1.5"><Droplets className="h-4 w-4 text-cyan-300" /> {wx.current.humidity}% humidity</span>
              <span className="inline-flex items-center gap-1.5"><Wind className="h-4 w-4 text-cyan-300" /> {Math.round(wx.current.wind_mph)} mph wind</span>
            </div>
            <span className="ml-auto text-[9px] font-mono uppercase tracking-wider text-ink-muted">{wx.source}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {wx.daily.map((d) => (
              <div key={d.date} className="rounded-xl border border-line bg-bg-surface p-4" data-testid={`newsroom-weather-day-${d.date}`}>
                <div className="text-xs font-mono text-ink-muted">{d.date}</div>
                <div className="text-sm font-semibold text-ink-primary mt-1">{d.condition}</div>
                <div className="text-xs text-ink-secondary mt-1">High {Math.round(d.high)}° / Low {Math.round(d.low)}° · Rain {d.rain_chance ?? "—"}%</div>
              </div>
            ))}
          </div>

          <button onClick={writeScript} disabled={busy === "script"} data-testid="newsroom-weather-script-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 px-4 py-3 text-sm font-bold text-white hover:brightness-110 transition disabled:opacity-60">
            {busy === "script" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Write Radio Weather Break (from this real data)
          </button>

          {script && (
            <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="newsroom-weather-script-box">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-cyan-300">On-air weather break · saved to stories</span>
                <button onClick={() => { navigator.clipboard.writeText(script); toast.success("Copied"); }}
                  className="inline-flex items-center gap-1 text-[11px] text-ink-muted hover:text-ink-primary transition">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <div className="whitespace-pre-wrap text-sm text-ink-secondary leading-relaxed">{script}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
